import { CollapsibleSection, Spinner } from '@consigli/facade';
import {
  type DocumentContradictionAgreementLink,
  type DocumentContradictionChunkLink,
  type DocumentContradiction as DocumentContradictionType,
  type DocumentPackage,
  type MeetingAgreement,
} from '@consigli/types';
import { useTranslation } from 'react-i18next';

import {
  calculateHighestSeverity,
  ConflictTypeBadge,
  CopyButton,
  DocumentSourceCard,
  DocumentSourceModal,
  DocumentSourcePrefetch,
  EmptyState,
  ErrorAlert,
  getSeverityCardStyles,
  LinkedAgreementCard,
  ProvisionalBadge,
  SeverityBadge,
  StatusBadge,
} from './components';
import { TranscriptSourceModal } from './components/transcript-source-modal';
import { useAgreementTranscriptSource } from './hooks/use-agreement-transcript-source';
import { useDocumentSource } from './hooks/use-document-source';
import { formatContradictionsForCopy } from './utils';

import type { Meeting, ParsedTranscriptEntry } from './utils/export-types';

interface DocumentContradictionProps {
  loading: boolean;
  generating?: boolean;
  hasFetched?: boolean;
  error: string | null;
  documentContradictions: DocumentContradictionType[];
  hasDocumentPackage: boolean;
  canRetry?: boolean;
  onRetry: () => void;
  documentPackages: DocumentPackage[];
  selectedPackageId: string | null;
  onPackageChange: (packageId: string) => void;
  meeting?: Meeting;
  agreements?: MeetingAgreement[];
  transcriptEntries?: ParsedTranscriptEntry[];
}

export const DocumentContradiction = ({
  loading,
  generating = false,
  hasFetched = true,
  error,
  documentContradictions,
  hasDocumentPackage,
  canRetry = true,
  onRetry,
  documentPackages,
  selectedPackageId,
  onPackageChange,
  meeting,
  agreements = [],
  transcriptEntries,
}: DocumentContradictionProps) => {
  const { t } = useTranslation();
  const isLoadingOrGenerating = loading || generating;
  const documentSource = useDocumentSource();
  const transcriptSource = useAgreementTranscriptSource(meeting, agreements, transcriptEntries);

  const severityLabels = {
    high: t('meeting-secretary.contradictions.severity-high'),
    medium: t('meeting-secretary.contradictions.severity-medium'),
    low: t('meeting-secretary.contradictions.severity-low'),
  };

  return (
    <div className="space-y-4">
      {documentPackages.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <label
            htmlFor="package-select"
            className="text-sm font-medium text-gray-700 whitespace-nowrap"
          >
            {t('meeting-secretary.contradictions.document-package')}:
          </label>
          <select
            id="package-select"
            value={selectedPackageId || ''}
            onChange={(e) => onPackageChange(e.target.value)}
            disabled={isLoadingOrGenerating}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-meeting-600 focus:border-meeting-600 disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            {documentPackages.map((pkg) => (
              <option key={pkg.id} value={pkg.id}>
                {pkg.name}
              </option>
            ))}
          </select>
          <span className="text-xs text-gray-500">
            {t('meeting-secretary.contradictions.packages-available', {
              count: documentPackages.length,
            })}
          </span>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-4">
          <Spinner size="xsmall" />
          <span className="ml-2 text-gray-600 text-sm">
            {t('meeting-secretary.contradictions.loading-document')}
          </span>
        </div>
      )}

      {!loading && generating && (
        <div className="flex items-center justify-center py-4">
          <Spinner size="xsmall" />
          <span className="ml-2 text-gray-600 text-sm">
            {t('meeting-secretary.contradictions.generating-document')}
          </span>
        </div>
      )}

      {error && (
        <ErrorAlert
          title={t('meeting-secretary.contradictions.failed-document')}
          message={error}
          onRetry={canRetry ? onRetry : undefined}
          retryLabel={t('meeting-secretary.contradictions.try-again')}
        />
      )}

      {!isLoadingOrGenerating && !error && !hasDocumentPackage && (
        <EmptyState
          icon="📄"
          title={t('meeting-secretary.contradictions.no-document-packages-title')}
          description={t('meeting-secretary.contradictions.no-document-packages-description')}
        />
      )}

      {!isLoadingOrGenerating &&
        !error &&
        hasFetched &&
        hasDocumentPackage &&
        documentContradictions.length === 0 && (
          <EmptyState
            icon="📋"
            title={t('meeting-secretary.contradictions.no-contradictions-title')}
            description={t(
              'meeting-secretary.contradictions.no-document-contradictions-description',
            )}
          />
        )}

      {!loading && !generating && !error && documentContradictions.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-gray-900">
              {t('meeting-secretary.contradictions.document-contradictions-found', {
                count: documentContradictions.length,
              })}
            </h3>
            <CopyButton
              textToCopy={formatContradictionsForCopy(
                documentContradictions,
                'Document Contradiction',
                t,
              )}
            />
          </div>

          {documentContradictions.map((contradiction) => {
            const links = contradiction.agreementLinks || [];
            const chunkLinks = (contradiction.chunkLinks || []) as DocumentContradictionChunkLink[];
            const highestSeverity = calculateHighestSeverity(links);

            return (
              <div
                key={contradiction.id}
                className={`p-4 border rounded-lg ${getSeverityCardStyles(highestSeverity)}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs px-2 py-1 rounded-full font-medium bg-meeting-700 text-white">
                      {t('meeting-secretary.contradictions.document-contradiction')}
                    </span>
                    {contradiction.conflictType && (
                      <ConflictTypeBadge conflictType={contradiction.conflictType} variant="blue" />
                    )}
                    <SeverityBadge severity={highestSeverity} severityLabels={severityLabels} />
                    {contradiction.status && <StatusBadge status={contradiction.status} />}
                    {contradiction.provisional && <ProvisionalBadge />}
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">{contradiction.title}</h4>
                    <p className="text-sm text-gray-700">
                      {contradiction.contradictionDescription}
                    </p>
                  </div>

                  {links.length > 0 && (
                    <CollapsibleSection
                      className="border-t border-gray-200 pt-3 mt-3"
                      label={t(
                        links.length === 1
                          ? 'meeting-secretary.contradictions.linked-agreement'
                          : 'meeting-secretary.contradictions.linked-agreements',
                        { count: links.length },
                      )}
                    >
                      {links.map((link: DocumentContradictionAgreementLink) => (
                        <LinkedAgreementCard
                          key={link.agreementId}
                          {...link}
                          showAgreementLabel
                          onShowSource={
                            transcriptSource.hasTranscript
                              ? () => transcriptSource.showSource(link)
                              : undefined
                          }
                        />
                      ))}
                    </CollapsibleSection>
                  )}

                  {chunkLinks.length > 0 && (
                    <CollapsibleSection
                      className="border-t border-gray-200 pt-3 mt-3"
                      label={t(
                        chunkLinks.length === 1
                          ? 'meeting-secretary.contradictions.document-source'
                          : 'meeting-secretary.contradictions.document-sources',
                        { count: chunkLinks.length },
                      )}
                    >
                      <DocumentSourcePrefetch chunks={chunkLinks} />
                      {chunkLinks.map((chunk: DocumentContradictionChunkLink) => (
                        <DocumentSourceCard
                          key={chunk.id}
                          {...chunk}
                          isLoadingSource={
                            documentSource.isLoading && documentSource.sourceChunk?.id === chunk.id
                          }
                          onViewSource={() => documentSource.showSource(chunk)}
                        />
                      ))}
                    </CollapsibleSection>
                  )}

                  {contradiction.notes && (
                    <div className="bg-gray-100 p-3 rounded mt-2">
                      <p className="text-xs text-gray-500 font-medium mb-1">
                        {t('meeting-secretary.contradictions.notes')}:
                      </p>
                      <p className="text-sm text-gray-700">{contradiction.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TranscriptSourceModal
        isOpen={!!transcriptSource.sourceRef}
        onClose={transcriptSource.closeSource}
        meeting={transcriptSource.meeting}
        entries={transcriptSource.entries}
        segmentIds={transcriptSource.segmentIds}
        isLoading={transcriptSource.isLoading}
        hasError={transcriptSource.hasError}
        onRetry={transcriptSource.retry}
      />

      <DocumentSourceModal
        chunk={documentSource.sourceChunk}
        readUrl={documentSource.readUrl}
        hasError={documentSource.hasError}
        isUnconvertible={documentSource.isUnconvertible}
        onClose={documentSource.closeSource}
      />
    </div>
  );
};
